import React, { useState } from 'react';

/**
 * TotpEnrollWidget
 * Props:
 *   enrollment: { factor_id, display: { qr_data_url, manual_seed, issuer, digits } }
 *   onActivate(factorId, code): Promise — controller handles the API call
 *   onCancel(): void
 */
export default function TotpEnrollWidget({ enrollment, onActivate, onCancel }) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showManual, setShowManual] = useState(false);

  const { factor_id, display } = enrollment;
  const digits = display.digits || 6;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (code.replace(/\s/g, '').length !== digits) {
      setError(`Please enter the ${digits}-digit code from your authenticator app`);
      return;
    }
    setSubmitting(true);
    try {
      await onActivate(factor_id, code.replace(/\s/g, ''));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Activation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">
          Set up authenticator app
        </h4>
        <p className="text-xs text-gray-500 mt-1">
          Scan the QR code with Google Authenticator, Authy, 1Password, or any TOTP-compatible app.
        </p>
      </div>

      <div className="flex flex-col items-center">
        <img
          src={display.qr_data_url}
          alt="TOTP QR Code"
          className="w-48 h-48 border border-gray-200 rounded"
        />
        <button
          type="button"
          onClick={() => setShowManual(!showManual)}
          className="mt-3 text-xs text-blue-600 hover:underline"
        >
          {showManual ? 'Hide manual setup key' : "Can't scan? Show manual setup key"}
        </button>
        {showManual && (
          <div className="mt-2 font-mono text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200 select-all">
            {display.manual_seed}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Enter {digits}-digit code
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9 ]*"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={digits + 2}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-lg tracking-widest text-center"
            placeholder={'•'.repeat(digits)}
            disabled={submitting}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm" role="alert">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !code}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Verifying…' : 'Verify & Activate'}
          </button>
        </div>
      </form>
    </div>
  );
}
